import { useState, useEffect, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { fetcher } from "../../../utils/fetcher";
import FlowTextInput from "../../FlowTextInput/FlowTextInput";
import FlowButton from "../../FlowButton/FlowButton";
import Spinner from "../../Spinner/Spinner";
import "./NoteEditorModal.scss";

function NoteEditorModal({ noteId, onNoteUpdated }) {
  const [note, setNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const lastSavedNoteData = useRef({ title: "", content: "" });
  const editorScrollableRef = useRef(null);
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: "resizable-image",
        },
      }),
    ],
    content: "",
    editable: true,
    editorProps: {
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith("image/"));

        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Check if clicking in corners
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isBottomLeft || isTopRight || isTopLeft) {
              event.preventDefault();

              const startX = event.clientX;
              const startY = event.clientY;
              const startWidth = img.offsetWidth;
              const aspectRatio = img.naturalHeight / img.naturalWidth;

              const handleMouseMove = (e) => {
                const diffX = e.clientX - startX;
                const diffY = e.clientY - startY;

                // Use the larger absolute difference to maintain aspect ratio
                const diff =
                  Math.abs(diffX) > Math.abs(diffY)
                    ? diffX
                    : diffY / aspectRatio;

                // Apply direction multiplier for left corners
                const directionMultiplier = isBottomLeft || isTopLeft ? -1 : 1;

                const newWidth = Math.max(
                  100,
                  Math.min(
                    startWidth + diff * directionMultiplier,
                    view.dom.offsetWidth - 40,
                  ),
                );

                img.style.width = newWidth + "px";
                img.style.height = "auto";
              };

              const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.body.style.cursor = "";

                // Update the image node with new width
                const pos = view.posAtDOM(img, 0);
                if (pos !== null && pos !== undefined) {
                  const node = view.state.doc.nodeAt(pos);
                  if (node && node.type.name === "image") {
                    view.dispatch(
                      view.state.tr.setNodeMarkup(pos, null, {
                        ...node.attrs,
                        width: img.style.width,
                      }),
                    );
                  }
                }
              };

              // Set appropriate cursor
              if (isBottomRight || isTopLeft) {
                document.body.style.cursor = "nwse-resize";
              } else {
                document.body.style.cursor = "nesw-resize";
              }

              document.addEventListener("mousemove", handleMouseMove);
              document.addEventListener("mouseup", handleMouseUp);
            }
          }
          return false;
        },
        mousemove: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Update cursor based on position
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isTopLeft) {
              img.style.cursor = "nwse-resize";
            } else if (isBottomLeft || isTopRight) {
              img.style.cursor = "nesw-resize";
            } else {
              img.style.cursor = "default";
            }
          }
          return false;
        },
      },
    },
  });

  const handleImageUpload = async (file) => {
    if (!note || !editor) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Only image files are allowed");
      return;
    }

    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetcher.post(
        `/api/notes/${note._id}/upload-image`,
        formData,
      );

      if (response.success && response.image) {
        // Insert the thumbnail image into the editor with alt text
        editor
          .chain()
          .focus()
          .setImage({
            src: response.image.thumbnailUrl,
            alt: response.image.filename,
          })
          .run();
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleUpdateNote = useCallback(
    async (isBackground = false) => {
      if (!note || !editor) return;

      const updatedTitle = noteTitle.trim();
      const updatedContent = editor.getHTML();

      if (isBackground) {
        // Background save: fire and forget, no UI feedback, no waiting
        fetcher
          .put(`/api/notes/${note._id}`, {
            title: noteTitle,
            content: updatedContent,
          })
          .then(() => {
            lastSavedNoteData.current = {
              title: updatedTitle,
              content: updatedContent,
            };
            if (onNoteUpdated) {
              onNoteUpdated({
                ...note,
                title: updatedTitle,
                content: updatedContent,
              });
            }
          })
          .catch((err) => {
            console.error("Error updating note (background):", err);
          });
        return;
      }

      // Manual save: show feedback and wait for response
      setIsUploading(true);
      const minimumSpinTime = new Promise((resolve) =>
        setTimeout(resolve, 1000),
      );

      try {
        await fetcher.put(`/api/notes/${note._id}`, {
          title: noteTitle,
          content: updatedContent,
        });

        lastSavedNoteData.current = {
          title: updatedTitle,
          content: updatedContent,
        };

        if (onNoteUpdated) {
          onNoteUpdated({
            ...note,
            title: updatedTitle,
            content: updatedContent,
          });
        }
      } catch (err) {
        console.error("Error updating note:", err);
      } finally {
        await minimumSpinTime;
        setIsUploading(false);
      }
    },
    [note, editor, noteTitle, onNoteUpdated],
  );

  // Fetch the note data
  useEffect(() => {
    if (noteId) {
      setIsLoading(true);
      fetcher
        .get(`/api/notes/${noteId}`)
        .then((data) => {
          setNote(data);
          setNoteTitle(data.title || "");
          lastSavedNoteData.current = {
            title: data.title?.trim() || "",
            content: data.content || "",
          };
          if (editor) {
            editor.commands.setContent(data.content || "");
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching note:", err);
          setIsLoading(false);
        });
    }
  }, [noteId, editor]);

  // Auto-save interval
  useEffect(() => {
    if (!autoUpdateEnabled || !note || !editor) return;

    const interval = setInterval(() => {
      const currentTitle = noteTitle.trim();
      const currentContent = editor.getHTML();

      if (
        currentTitle !== lastSavedNoteData.current.title ||
        currentContent !== lastSavedNoteData.current.content
      ) {
        handleUpdateNote(true); // Auto-save in background
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [autoUpdateEnabled, note, editor, noteTitle, handleUpdateNote]);

  if (isLoading) {
    return (
      <div className="note-editor-modal">
        <Spinner />
      </div>
    );
  }

  if (!note) {
    return (
      <div className="note-editor-modal">
        <div className="empty-state">Note not found</div>
      </div>
    );
  }

  return (
    <div className="note-editor-modal">
      <div className="note-title-bar">
        <FlowTextInput
          className="note-title-flow-input"
          placeholder="Note title..."
          value={noteTitle}
          onChange={(value) => setNoteTitle(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleUpdateNote();
            }
          }}
          expandable={false}
          icon=""
        />
        <FlowButton variant="update" onClick={handleUpdateNote}>
          <span style={{ position: "relative", top: "1px" }}>Save</span>
          {autoUpdateEnabled && (
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: "16px",
                marginLeft: "4px",
                verticalAlign: "middle",
                animation: isUploading ? "spin 1s ease-in-out" : "none",
              }}
            >
              cached
            </span>
          )}
        </FlowButton>
        <label className="auto-save-label">
          <input
            type="checkbox"
            checked={autoUpdateEnabled}
            onChange={(e) => setAutoUpdateEnabled(e.target.checked)}
          />
          Auto-save
        </label>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div className="note-editor-wrapper">
        <div className="note-editor-controls">
          <button
            className="editor-btn"
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <span className="editor-btn-text">B</span>
          </button>
          <button
            className="editor-btn"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <span className="editor-btn-text" style={{ fontStyle: "italic" }}>
              I
            </span>
          </button>
          <button
            className="editor-btn"
            onClick={() => editor?.chain().focus().toggleStrike().run()}
          >
            <span
              className="editor-btn-text"
              style={{ textDecoration: "line-through" }}
            >
              S
            </span>
          </button>
          <span className="editor-separator" />
          <button
            className="editor-btn"
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 1 }).run()
            }
          >
            <span className="editor-btn-text">H1</span>
          </button>
          <button
            className="editor-btn"
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <span className="editor-btn-text">H2</span>
          </button>
          <button
            className="editor-btn"
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            <span className="editor-btn-text">H3</span>
          </button>
          <span className="editor-separator" />
          <button
            className="editor-btn"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <span className="material-symbols-outlined">
              format_list_bulleted
            </span>
          </button>
          <button
            className="editor-btn"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <span className="material-symbols-outlined">
              format_list_numbered
            </span>
          </button>
          <span className="editor-separator" />
          <button
            className="editor-btn"
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          >
            <span className="editor-btn-text">99</span>
          </button>
          <button
            className="editor-btn"
            onClick={() => {
              if (!editor) return;

              const { from, to } = editor.state.selection;
              const selectedText = editor.state.doc.textBetween(from, to, "\n");

              // If we have selected text across multiple paragraphs
              if (selectedText && selectedText.includes("\n")) {
                editor
                  .chain()
                  .focus()
                  .deleteSelection()
                  .insertContent({
                    type: "codeBlock",
                    content: [{ type: "text", text: selectedText }],
                  })
                  .run();
              } else {
                // Normal toggle for single line or empty
                editor.chain().focus().toggleCodeBlock().run();
              }
            }}
          >
            <span className="editor-btn-text">&lt;&gt;</span>
          </button>
          <span className="editor-separator" />
          <button
            className="editor-btn"
            onClick={handleImageButtonClick}
            disabled={isUploadingImage}
            title="Upload image"
          >
            <span className="material-symbols-outlined">
              {isUploadingImage ? "hourglass_empty" : "image"}
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
        <div
          ref={editorScrollableRef}
          className="note-editor-scrollable"
          onClick={() => {
            if (editor && !editor.isFocused) {
              editor.commands.focus("end");
            }
          }}
        >
          <EditorContent editor={editor} className="note-content-area" />
        </div>
      </div>
    </div>
  );
}

export default NoteEditorModal;
